import {
  ChevronRight,
  KeyboardArrowDown,
  KeyboardArrowLeft,
} from "@mui/icons-material";
import {
  ClickAwayListener,
  Collapse,
  Divider,
  Paper,
  Stack,
} from "@mui/material";
import { CustomProgressBar, CustomText, PaperButton } from "components";
import Layout from "layouts";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { openLoading } from "store/reducers/tools";
import {
  DEFAULT_ELEVATION,
  curWeekNum,
  curYearNum,
  errorAction,
  formatArray,
  getDayName,
  getWeekDaysByWeekNumber,
  notify,
  serverPost,
  weekCount,
} from "utility";
import { DispatchHeader } from "./DispatchHeader";

export function Dispatch() {
  const history = useNavigate();
  const dispatch = useDispatch();
  const [weeks, setWeeks] = useState([]);
  const [selWeek, setSelWeek] = useState({});
  const [weekOpen, setWeekOpen] = useState(false);
  const [weeklySlots, setWeeklySlots] = useState([]);
  const [yearOpen, setYearOpen] = useState(false);
  const [selYear, setSelYear] = useState({
    id: curYearNum,
    label: curYearNum.toString(),
  });

  const handleSelectWeekDay = (day, key, boxes = []) => {
    if (formatArray(boxes).length === 0) {
      notify({ message: "There is no box!", variant: "warning" });
      return;
    }
    dispatch({
      type: "SET_APP",
      payload: {
        selWeek: selWeek,
        selDay: key,
        label: day,
        boxes: boxes,
        selYear: selYear?.id,
      },
    });
    history({
      pathname: "/day-overview",
      state: {
        selWeek: selWeek,
        selDay: key,
        label: day,
        selYear: selYear?.id,
      },
    });
  };

  const setCurrentYearWeeks = () => {
    const weeklyInfos = Array.from(Array(weekCount).keys()).reduce(
      (ret, cur, index) => {
        const label = `Week ${cur + 1} (${getWeekDaysByWeekNumber({
          weekNumber: cur + 1,
          addDay: 0,
          format: "DD MMM",
          yearNumber: selYear?.id ?? curYearNum,
        })} ~ ${getWeekDaysByWeekNumber({
          weekNumber: cur + 1,
          addDay: 6,
          format: "DD MMM",
          yearNumber: selYear?.id ?? curYearNum,
        })})`;
        return [...ret, { id: cur + 1, label: label }];
      },
      []
    );
    setSelWeek(() => weeklyInfos[curWeekNum - 1]);
    setWeeks(() => weeklyInfos);
  };

  const loadData = () => {
    openLoading(true);
    serverPost({
      url: "weeklySlot/all",
      data: {
        weekNum: selWeek?.id ?? curWeekNum,
        weekYear: selYear?.id ?? curYearNum,
      },
      onFinish: (res, err) => {
        openLoading(false);
        if (res === false) return errorAction(res, err);
        setWeeklySlots(res);
      },
    });
  };

  useEffect(() => {
    setCurrentYearWeeks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selYear]);

  useEffect(() => {
    loadData();
    //eslint-disable-next-line
  }, [selWeek, selYear]);
  return (
    <Layout sidebar={false} noHeader={true}>
      <div>
        <DispatchHeader
          backButton={true}
          title={`Week ${selWeek?.id} - ${getWeekDaysByWeekNumber({
            weekNumber: selWeek?.id,
            addDay: 0,
            format: "DD MMMM",
          })} - ${getWeekDaysByWeekNumber({
            weekNumber: selWeek?.id,
            addDay: 5,
            format: "DD MMMM YYYY",
          })}`}
        />
        <div className="py-4 flex justify-between flex-wrap">
          <PaperButton
            className="px-6 py-2 text-xl"
            icon={<KeyboardArrowLeft />}
            iconPosition="start"
            onClick={() => {
              if (selWeek?.id === 1) return;
              setSelWeek(
                (s = {}) =>
                  weeks?.filter((item) => item?.id === s?.id - 1)?.[0] ?? {}
              );
            }}
          >
            Previous Week
          </PaperButton>
          <div className="flex">
            <div className="px-2">
              <div className="relative">
                <PaperButton
                  className="px-4 py-2 text-xl w-16"
                  icon={<KeyboardArrowDown />}
                  onClick={() => setYearOpen((s) => !s)}
                >
                  {selYear?.label ?? ""}
                </PaperButton>
                <Collapse in={yearOpen} unmountOnExit easing="fade">
                  <ClickAwayListener onClickAway={() => setYearOpen(false)}>
                    <Paper
                      className="absolute top-0 left-0 w-full max-h-96 overflow-auto mt-20 z-10"
                      elevation={DEFAULT_ELEVATION}
                    >
                      {formatArray(
                        Array.from(Array(4).keys()).reduce(
                          (ret, cur) => [
                            ...ret,
                            {
                              id: curYearNum - cur,
                              label: (curYearNum - cur).toString(),
                            },
                          ],
                          [
                            {
                              id: curYearNum + 1,
                              label: (curYearNum + 1).toString(),
                            },
                          ]
                        )
                      ).map((item, itemIndex) => (
                        <CustomText
                          className="p-2 cursor-pointer hover:bg-white-gray"
                          variant="body2"
                          key={itemIndex}
                          color="secondary"
                          onClick={() => {
                            setSelYear(item);
                            setYearOpen(false);
                          }}
                        >
                          {item?.label ?? ""}
                        </CustomText>
                      ))}
                    </Paper>
                  </ClickAwayListener>
                </Collapse>
              </div>
            </div>
            <div className="relative">
              <PaperButton
                className="px-6 py-2 text-xl"
                icon={<KeyboardArrowDown />}
                onClick={() => setWeekOpen((s) => !s)}
              >
                {selWeek?.label ?? ""}
              </PaperButton>
              <Collapse in={weekOpen} unmountOnExit easing="fade">
                <ClickAwayListener onClickAway={() => setWeekOpen(false)}>
                  <Paper
                    className="absolute top-0 left-0 w-full max-h-96 overflow-auto mt-20 z-10"
                    elevation={DEFAULT_ELEVATION}
                  >
                    {formatArray(weeks).map((item, itemIndex) => (
                      <CustomText
                        className="p-2 cursor-pointer hover:bg-white-gray"
                        variant="body2"
                        key={itemIndex}
                        color="secondary"
                        onClick={() => {
                          setSelWeek(item);
                          setWeekOpen(false);
                        }}
                      >
                        {item?.label ?? ""}
                      </CustomText>
                    ))}
                  </Paper>
                </ClickAwayListener>
              </Collapse>
            </div>
          </div>
          <PaperButton
            className="px-5 py-2 text-xl"
            icon={<ChevronRight />}
            onClick={() => {
              if (selWeek?.id === weekCount) return;
              setSelWeek(
                (s = {}) =>
                  weeks?.filter((item) => item?.id === s?.id + 1)?.[0] ?? {}
              );
            }}
          >
            Next Week
          </PaperButton>
        </div>
        <div className="">
          {Array.from(Array(5).keys()).map((key) => {
            const curDay = Boolean(
              moment().format("DD") ===
                getWeekDaysByWeekNumber({
                  weekNumber: selWeek?.id,
                  addDay: key,
                  format: "DD",
                  yearNumber: selYear?.id,
                })
            );
            const day = getWeekDaysByWeekNumber({
              weekNumber: selWeek?.id,
              addDay: key,
              format: "Do of MMMM",
              yearNumber: selYear?.id ?? curYearNum,
            });
            const dayData = formatArray(weeklySlots)?.filter(
              (item) => item?.deliveryDate?.weekDay - 1 === key + 1
            );
            const completedBoxes = formatArray(dayData)?.filter((item) =>
              Boolean(item?.trackingInfo?.consignmentId)
            );
            return (
              <Paper
                className={`flex my-2 cursor-pointer ${
                  curDay ? "bg-blue-100" : ""
                } hover:bg-blue-100`}
                key={key}
                onClick={() => handleSelectWeekDay(day, key, dayData)}
              >
                <div className="border-r border-white-gray w-1/5 flex flex-col justify-center pl-4">
                  <CustomText className="p-1" color="secondary" variant="body2">
                    {day}
                  </CustomText>
                  <CustomText
                    className="p-1"
                    color="secondary"
                    variant="h5"
                    bold
                  >
                    {getDayName(key + 1)}
                  </CustomText>
                </div>
                <Stack
                  className="p-4 flex-1"
                  direction="row"
                  spacing={2}
                  divider={
                    <Divider
                      className="border-r border-white-gray"
                      orientation="vertical"
                      flexItem
                    />
                  }
                >
                  <div className="w-1/3">
                    <CustomText
                      className="p-1"
                      color="secondary"
                      variant="body2"
                    >
                      Number of boxes:
                    </CustomText>
                    <CustomText
                      className="p-1"
                      color="secondary"
                      variant="h5"
                      bold
                    >
                      {`${completedBoxes?.length} / ${dayData?.length}`}
                    </CustomText>
                  </div>
                  <div className="w-2/3 flex flex-col justify-between">
                    <CustomText
                      className="p-1"
                      color="secondary"
                      variant="body2"
                    >
                      Percentage fulfilled:
                    </CustomText>
                    <div className="flex items-center">
                      <div className="flex-1">
                        <CustomProgressBar
                          variant="determinate"
                          value={
                            (completedBoxes?.length / dayData?.length) * 100
                          }
                          height={10}
                        />
                      </div>
                      <CustomText className="pl-2" color="secondary">
                        {dayData?.length === 0
                          ? "0%"
                          : `${(
                              (completedBoxes?.length / dayData?.length) *
                              100
                            ).toFixed(2)}%`}
                      </CustomText>
                    </div>
                  </div>
                </Stack>
              </Paper>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
